/**
 * URL-driven window state for the engineering telemetry view. Mirrors
 * the vocabulary of DeviceStateTimeline's `useTimelineUrlState` so an
 * operator who's learned one chart's interaction model gets the other
 * for free. Both should be unified into a generic `useChartUrlState`
 * in a follow-up — kept separate here to avoid touching the verified
 * timeline implementation in the same PR.
 *
 * URL params:
 *   ?range=<key>   — preset key; omitted when key is the default (24h)
 *   ?span=<ms>     — custom (brushed) span in ms; replaces `range`
 *   ?until=<ISO>   — window right edge; omitted when isLive
 *   ?hide=<ids>    — comma-separated series ids that should be hidden
 *                     in the chart, tooltip, and legend
 *
 * All writes go through `useSearchParams` with `replace: true` so
 * panning / brushing / Jump-to don't flood browser history.
 *
 * Each preset maps to a matview bucket — backed by /devices/:id/telemetry's
 * `bucket` query param. The mapping is fixed (server-side aggregation
 * granularity is opaque to the user):
 *   1h, 6h, 24h → 5m bucket
 *   7d          → 1h bucket
 *   30d, 90d    → 1d bucket
 */
import { useSearchParams } from 'react-router-dom';
import type { TelemetryBucket } from '../../api/hooks';
import { parseHide, serializeHide } from './series';

export type TelemetryRangeKey =
  | '1h'
  | '6h'
  | '24h'
  | '7d'
  | '30d'
  | '90d';

export interface TelemetryRangePreset {
  key: TelemetryRangeKey;
  hours: number;
  label: string;
  bucket: TelemetryBucket;
}

export const TELEMETRY_RANGES: TelemetryRangePreset[] = [
  { key: '1h', hours: 1, label: '1 hour', bucket: '5m' },
  { key: '6h', hours: 6, label: '6 hours', bucket: '5m' },
  { key: '24h', hours: 24, label: '24 hours', bucket: '5m' },
  { key: '7d', hours: 168, label: '7 days', bucket: '1h' },
  { key: '30d', hours: 720, label: '30 days', bucket: '1d' },
  { key: '90d', hours: 2160, label: '90 days', bucket: '1d' },
];

export const TELEMETRY_DEFAULT_RANGE: TelemetryRangeKey = '24h';
export const TELEMETRY_MIN_CUSTOM_SPAN_MS = 15 * 60 * 1000; // 15 min floor

export function telemetrySpanForKey(key: TelemetryRangeKey): number {
  return TELEMETRY_RANGES.find((r) => r.key === key)!.hours * 3600 * 1000;
}

/**
 * Pick the right matview bucket for a custom (brushed) span. Matches the
 * preset mapping so a brushed 24-hour window gets the same 5m granularity
 * the `24h` preset would.
 */
export function telemetryBucketForSpan(spanMs: number): TelemetryBucket {
  const hours = spanMs / 3_600_000;
  if (hours <= 24) return '5m';
  if (hours <= 24 * 14) return '1h';
  return '1d';
}

function nearestPresetTo(spanMs: number): TelemetryRangeKey {
  let best: TelemetryRangeKey = TELEMETRY_DEFAULT_RANGE;
  let bestDist = Infinity;
  for (const r of TELEMETRY_RANGES) {
    const d = Math.abs(r.hours * 3600 * 1000 - spanMs);
    if (d < bestDist) {
      bestDist = d;
      best = r.key;
    }
  }
  return best;
}

export interface TelemetryUrlState {
  /** Preset key, or `'custom'` when the window was set by brush-zoom. */
  rangeKey: TelemetryRangeKey | 'custom';
  /** Resolved span in milliseconds (preset hours or custom span). */
  spanMs: number;
  /** Centre of the window in ms-since-epoch, or null when tracking live. */
  centreMs: number | null;
  /** Matview bucket appropriate for the current span. */
  bucket: TelemetryBucket;
  /** Set of series ids the operator has chosen to hide. */
  hidden: Set<string>;
  /** Switch to a preset range. Clears any custom span. */
  setRangeKey: (key: TelemetryRangeKey) => void;
  /** Move the window centre. Pass null to snap back to live. */
  setCentreMs: (next: number | null | ((c: number | null) => number | null)) => void;
  /** Commit a brushed window — centre + span. Clamps span to the floor. */
  setCustomWindow: (centreMs: number, spanMs: number) => void;
  /** Exit a brushed window — snap to the nearest preset range. */
  resetCustom: () => void;
  /** Toggle a series id in the hidden set. */
  toggleHide: (seriesId: string) => void;
  /** Force a specific hidden set (used by "Show all" links). */
  setHidden: (next: Set<string>) => void;
}

export function useTelemetryUrlState(): TelemetryUrlState {
  const [params, setParams] = useSearchParams();
  const urlRange = params.get('range');
  const urlSpan = params.get('span');
  const urlUntil = params.get('until');
  const urlHide = params.get('hide');

  let rangeKey: TelemetryRangeKey | 'custom';
  let spanMs: number;
  const parsedSpan = urlSpan ? Number(urlSpan) : NaN;
  if (Number.isFinite(parsedSpan) && parsedSpan >= TELEMETRY_MIN_CUSTOM_SPAN_MS) {
    rangeKey = 'custom';
    spanMs = parsedSpan;
  } else {
    const preset =
      TELEMETRY_RANGES.find((r) => r.key === urlRange)?.key ??
      TELEMETRY_DEFAULT_RANGE;
    rangeKey = preset;
    spanMs = telemetrySpanForKey(preset);
  }
  const halfSpanMs = spanMs / 2;

  let centreMs: number | null = null;
  if (urlUntil) {
    const t = new Date(urlUntil).getTime();
    if (Number.isFinite(t)) centreMs = t - halfSpanMs;
  }

  const hidden = parseHide(urlHide);
  const bucket =
    rangeKey === 'custom'
      ? telemetryBucketForSpan(spanMs)
      : TELEMETRY_RANGES.find((r) => r.key === rangeKey)!.bucket;

  function writeUrl(next: {
    rangeKey?: TelemetryRangeKey | 'custom';
    spanMs?: number;
    centreMs?: number | null;
    hidden?: Set<string>;
  }) {
    const nextRange = next.rangeKey ?? rangeKey;
    const nextSpan = next.spanMs ?? spanMs;
    const nextCentre = 'centreMs' in next ? (next.centreMs ?? null) : centreMs;
    const nextHidden = next.hidden ?? hidden;

    const np = new URLSearchParams(params);
    if (nextRange === 'custom') {
      np.delete('range');
      np.set('span', String(Math.round(nextSpan)));
    } else {
      np.delete('span');
      if (nextRange === TELEMETRY_DEFAULT_RANGE) np.delete('range');
      else np.set('range', nextRange);
    }
    if (nextCentre === null) {
      np.delete('until');
    } else {
      np.set('until', new Date(nextCentre + nextSpan / 2).toISOString());
    }
    const hideStr = serializeHide(nextHidden);
    if (hideStr) np.set('hide', hideStr);
    else np.delete('hide');
    setParams(np, { replace: true });
  }

  return {
    rangeKey,
    spanMs,
    centreMs,
    bucket,
    hidden,
    setRangeKey: (key) => writeUrl({ rangeKey: key }),
    setCentreMs: (next) => {
      const resolved = typeof next === 'function' ? next(centreMs) : next;
      writeUrl({ centreMs: resolved });
    },
    setCustomWindow: (newCentre, newSpan) =>
      writeUrl({
        rangeKey: 'custom',
        spanMs: Math.max(newSpan, TELEMETRY_MIN_CUSTOM_SPAN_MS),
        centreMs: newCentre,
      }),
    resetCustom: () =>
      writeUrl({ rangeKey: nearestPresetTo(spanMs), spanMs: undefined }),
    toggleHide: (seriesId) => {
      const nextHidden = new Set(hidden);
      if (nextHidden.has(seriesId)) nextHidden.delete(seriesId);
      else nextHidden.add(seriesId);
      writeUrl({ hidden: nextHidden });
    },
    setHidden: (next) => writeUrl({ hidden: next }),
  };
}
